/**
 * LPAssetSelector — Modal for selecting an existing Landing Page asset
 * 
 * Used by "Link Existing" and "Duplicate Existing" flows in the PPB LP mini-step.
 * Shows LP name, use case, confidence, status, and updated_at.
 * 
 * Props:
 *   open        — boolean, modal visibility
 *   onClose     — close handler
 *   onSelect    — (lp) => void, called when user picks an LP
 *   mode        — "link" | "duplicate" — determines action label
 *   tenantId    — tenant context
 */

import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import {
  Link2, Copy, Search, Layers, Loader2, X,
} from "lucide-react";

const STATUS_BADGE = {
  draft: "bg-slate-500/20 text-slate-300 border-slate-500/30",
  active: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  archived: "bg-amber-500/20 text-amber-300 border-amber-500/30",
};

export function LPAssetSelector({ open, onClose, onSelect, mode = "link", tenantId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!open || !tenantId) return;
    setLoading(true);
    api
      .get(`/landing-pages/list?tenant_id=${tenantId}&limit=100`)
      .then((res) => setItems(res.data?.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [open, tenantId]);

  if (!open) return null;

  const filtered = items.filter((lp) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (
      (lp.name || "").toLowerCase().includes(q) ||
      (lp.metadata?.primary_use_case || "").toLowerCase().includes(q)
    );
  });

  const ActionIcon = mode === "link" ? Link2 : Copy;
  const actionLabel = mode === "link" ? "Link" : "Duplicate";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="lp-asset-selector-modal">
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-lg mx-4 bg-[#0d1526] border border-white/10 rounded-xl shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-white/8">
          <div className="flex items-center gap-2.5">
            <ActionIcon className="w-4.5 h-4.5 text-rose-400" />
            <span className="text-sm font-semibold text-white">
              {mode === "link" ? "Link Existing Landing Page" : "Duplicate Landing Page"}
            </span>
          </div>
          <button onClick={onClose} className="p-1 rounded-md hover:bg-white/8 text-slate-400">
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Search */}
        <div className="px-5 py-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-500" />
            <Input
              data-testid="lp-selector-search"
              placeholder="Search landing pages..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 bg-white/5 border-white/10 text-white text-sm h-9 placeholder:text-slate-500"
            />
          </div>
        </div>

        {/* List */}
        <ScrollArea className="max-h-[360px] px-5 pb-4">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading...
            </div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500 text-sm">
              {items.length === 0 ? "No landing pages found" : "No matches"}
            </div>
          ) : (
            <div className="space-y-2">
              {filtered.map((lp) => {
                const meta = lp.metadata || {};
                return (
                  <button
                    key={lp.id}
                    data-testid={`lp-selector-item-${lp.id}`}
                    onClick={() => onSelect(lp)}
                    className={cn(
                      "w-full text-left px-4 py-3 rounded-lg border border-white/8",
                      "bg-white/[0.02] hover:bg-white/[0.06] transition-colors",
                      "group cursor-pointer"
                    )}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <Layers className="w-3.5 h-3.5 text-rose-400 flex-shrink-0" />
                          <span className="text-sm font-medium text-white truncate">
                            {lp.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 flex-wrap mt-1.5">
                          <Badge variant="outline" className={cn("text-[10px] px-1.5 py-0", STATUS_BADGE[lp.status] || STATUS_BADGE.draft)}>
                            {lp.status}
                          </Badge>
                          {meta.primary_use_case && (
                            <span className="text-[10px] text-slate-500">
                              {meta.primary_use_case.replace(/_/g, " ")}
                            </span>
                          )}
                          {meta.use_case_confidence && (
                            <span className="text-[10px] text-slate-600">
                              ({meta.use_case_confidence})
                            </span>
                          )}
                        </div>
                        {meta.use_case_reason && (
                          <p className="text-[10px] text-slate-600 mt-1 truncate">
                            {meta.use_case_reason}
                          </p>
                        )}
                      </div>
                      <div className="flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Badge className="bg-rose-500/20 text-rose-300 border-rose-500/30 text-[10px]">
                          {actionLabel}
                        </Badge>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </div>
    </div>
  );
}
